import { ReactNode } from "react";

import { Link } from "components/elements";
import {
  GrabsonIcon,
  ImgIcon,
  TableRow,
  TableRowColumn,
} from "components/layout";
import {
  ReleaseStage,
  TiVoAppProvider,
  TiVoAppVariant,
  TiVoPublications,
} from "hooks/tivoApps";
import { TiVoDeviceClientType, TiVoDeviceOem } from "hooks/tivoDevices";
import { useToggle } from "hooks/utils";
import { TiVoAppRowVariantMenu } from "pages/TiVoApps/_shared";
import { getImageUrlFromAssets } from "pages/TiVoApps/utils";

import { IMAGES, MAIN_ROUTE } from "../../constants";
import styles from "./PublicationsTable.scss";

export type PublicationRenderer = (props: {
  publication: TiVoPublications;
  platformId: string;
  oemId: string;
  applicableReleaseStages: ReleaseStage[];
  clientTypeId?: string;
}) => ReactNode;

export type PublicationsTableRowCommonProps = {
  provider: TiVoAppProvider;
  variant: TiVoAppVariant;

  publication: TiVoPublications;
  platformId: string;
  oems: TiVoDeviceOem[];

  getOemClientTypes: (oem: TiVoDeviceOem) => TiVoDeviceClientType[];
};

export type PublicationsTableRowPanelProps = PublicationsTableRowCommonProps;

export type PublicationsTableRowProps = PublicationsTableRowCommonProps & {
  cellClassName?: string;

  renderRowPanel?: (props: PublicationsTableRowPanelProps) => ReactNode;
  children: PublicationRenderer;
};

export function PublicationsTableRow({
  provider,
  variant,
  publication,
  platformId,
  oems,
  cellClassName,
  getOemClientTypes,
  renderRowPanel,
  children,
}: PublicationsTableRowProps) {
  const [expanded, toggleExpanded] = useToggle(false);
  const hasPanel = renderRowPanel !== undefined;

  return (
    <>
      <TableRow key={publication.id} className={styles.bodyRow}>
        <TableRowColumn columnClass={styles.providerCell}>
          <div
            className={styles.expander}
            onClick={hasPanel ? toggleExpanded : undefined}
          >
            {hasPanel && (
              <GrabsonIcon
                className={styles.icon}
                name={expanded ? "arrow-up" : "arrow-down"}
                font="micro"
              />
            )}
          </div>
          <ImgIcon
            src={
              getImageUrlFromAssets(
                provider.assets,
                IMAGES.PRIMARY_BRANDING_LOGO_SQUARE
              ) ||
              getImageUrlFromAssets(
                provider.assets,
                IMAGES.PARTNER_SOURCE_LOGO_SQUARE
              )
            }
          />
          {provider.label}
        </TableRowColumn>
        <TableRowColumn columnClass={styles.variantCell}>
          <Link
            to={`/${MAIN_ROUTE}/details/${provider.id}/variant/${variant.id}`}
          >
            {variant.label}
          </Link>
        </TableRowColumn>
        {oems.map((oem) => {
          const clientTypes =
            getOemClientTypes(oem).length > 0
              ? getOemClientTypes(oem)
              : [undefined];
          return clientTypes.map((clientType) => (
            <TableRowColumn
              key={`${oem.id}-${clientType?.id || "all"}`}
              className={cellClassName}
            >
              {children({
                publication,
                platformId,
                oemId: oem.id,
                clientTypeId: clientType?.id,
                applicableReleaseStages: oem.applicableReleaseStages,
              })}
            </TableRowColumn>
          ));
        })}
        <TableRowColumn columnClass={styles.menuCell}>
          <TiVoAppRowVariantMenu id={provider.id} variantId={variant.id} />
        </TableRowColumn>
      </TableRow>
      {hasPanel &&
        expanded &&
        renderRowPanel({
          provider,
          variant,
          publication,
          platformId,
          oems,
          getOemClientTypes,
        })}
    </>
  );
}
